import { useContext, useState } from 'react';
import { CrazyAIContext } from '/src/contexts/CrazyAI';

import { ChatTranscript } from '../components/ChatTranscript';
import { HarnessSelector } from '../components/HarnessSelector';
import { HarnessSessionList } from '../components/HarnessSessionList';
import { useHarnessChat } from '../hooks/useHarnessChat';

export function HarnessPrototypePage() {
  const { ai } = useContext(CrazyAIContext);
  const [message, setMessage] = useState('');
  const chat = useHarnessChat(ai);
  const hasHarness = Boolean(chat.harnessId);
  const canSend = Boolean(ai?.chat) && hasHarness && !chat.isSending && message.trim().length > 0;

  function handleSubmit(event) {
    event.preventDefault();

    if (!canSend) {
      return;
    }

    chat.sendMessage(message);
    setMessage('');
  }

  function handleComposerKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    event.currentTarget.form?.requestSubmit();
  }

  return (
    <div className="min-h-full bg-solitude-500 px-6 py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="rounded-xl border border-mystic-500 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="mb-2 inline-flex rounded-full bg-dodger-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-dodger-blue-700">
                harness-prototype
              </div>
              <h1 className="text-3xl font-bold text-body-500">Harness Prototype</h1>
              <p className="mt-2 max-w-2xl text-sm text-body-300">
                Exercise CrazyAI harness chat from Shell with streaming responses, chat history, and client tools.
              </p>
            </div>
            <div className="w-full max-w-xs">
              <HarnessSelector harnessId={chat.harnessId} onChange={chat.setHarnessId} />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3 text-xs text-body-300">
            {hasHarness ? <span>chat_id: {chat.currentChatId || 'new'}</span> : null}
            <span>connection: {ai?.chat ? 'ready' : 'connecting'}</span>
          </div>
        </header>

        {chat.error ? (
          <div className="rounded-lg border border-carnation-500 bg-carnation-50 p-4 text-sm text-carnation-700">
            {chat.error}
          </div>
        ) : null}

        {hasHarness ? (
          <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
            <HarnessSessionList
              currentChatId={chat.currentChatId}
              isLoading={chat.isLoadingSessions}
              onNewChat={chat.startNewChat}
              onSelectChat={chat.loadChat}
              sessions={chat.sessions}
            />

            <main className="space-y-4">
              <ChatTranscript isLoading={chat.isLoadingChat} transcript={chat.transcript} />
              <form
                className="sticky bottom-4 rounded-xl border border-mystic-500 bg-white p-4 shadow-lg"
                onSubmit={handleSubmit}
              >
                <textarea
                  className="min-h-[120px] w-full resize-y rounded-lg border border-mystic-500 px-4 py-3 text-sm text-body-500 outline-none transition placeholder:text-body-300 focus:border-dodger-blue-500 focus:ring-2 focus:ring-dodger-blue-100"
                  disabled={!ai?.chat || chat.isSending}
                  onChange={(event) => setMessage(event.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  placeholder="Ask the harness something…"
                  value={message}
                />
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-body-300">Enter sends. Shift+Enter adds a newline.</div>
                  <button
                    className="rounded-md bg-dodger-blue-500 px-5 py-2 text-sm font-semibold text-white transition hover:bg-dodger-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!canSend}
                    type="submit"
                  >
                    {chat.isSending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </form>
            </main>
          </div>
        ) : (
          <div className="rounded-xl border border-mystic-500 bg-white p-8 text-center text-sm text-body-300 shadow-sm">
            Select a harness to load sessions and start chatting.
          </div>
        )}
      </div>
    </div>
  );
}
